import { useCallback, useState } from 'react';
import JSZip from 'jszip';
import type { OpaqueVideoData } from '@lumen5/luminary';
import { OfflineAudioEncoder, VideoData } from '@lumen5/luminary';

import { Toast } from 'js/src/components/common/Toast';
import { FREE_PLAN_NAME } from 'js/src/libs/consts';
import { ErrorReporter } from 'js/src/libs/error-reporter';
import { withDataLuminary } from 'js/src/luminary/data-luminary';

const PREVIEW_RESOLUTION_VALUE = 512;
const FULL_RESOLUTION_VALUE = 1080;

type VideoArtifact = {
    videoData: OpaqueVideoData;
    videoName: string;
};

/**
 * Renders one PNG per scene and packs them into a zip.
 *
 * `fullResolution` selects between the two variants we offer:
 *  - false: a 512px preview render off whatever media is already available.
 *  - true: a 1080p render (Luminary's ceiling) off each asset's `renderUrl`. Those urls are only
 *    injected by the render pipeline, so this variant requires a published video -- see
 *    `canDownloadFullResolution` below.
 */
function useSceneImagesZipDownload({ fullResolution }: { fullResolution: boolean }) {
    const [downloading, setDownloading] = useState(false);

    const download = useCallback(
        async ({ videoData, videoName }: VideoArtifact) => {
            setDownloading(true);
            try {
                const zip = new JSZip();

                await withDataLuminary(
                    async (luminary) => {
                        // Untimed on purpose: an export is user-initiated and may legitimately
                        // load for minutes.
                        luminary.load(videoData);
                        await luminary.onCanPlay();
                        await luminary.onMatchCutsRehydrated();

                        let sceneIndex = 0;
                        for (const scene of luminary.scenes) {
                            const canvas = document.createElement('canvas');
                            const { width, height } = luminary.outputResolution;
                            canvas.width = width;
                            canvas.height = height;

                            try {
                                await scene.renderPreviewToCanvas(canvas, { subsceneIndex: 0 });
                            } catch (e) {
                                Toast.error(`Failed to render scene ${sceneIndex + 1}`);
                                continue;
                            }

                            const blob: Blob = await new Promise((resolve, reject) => {
                                canvas.toBlob((blobResult) => {
                                    if (!blobResult) {
                                        reject(
                                            new Error(
                                                `Failed to create blob for scene ${scene.id}`,
                                            ),
                                        );
                                    } else {
                                        resolve(blobResult);
                                    }
                                }, 'image/png');
                            });
                            zip.file(`scene_${++sceneIndex}.png`, blob);
                        }
                    },
                    {
                        luminarySettings: {
                            fetchResources: {
                                audio: false,
                                media: true,
                                transitions: false,
                                fonts: true,
                            },
                            disableRenderer: false,
                            resolutionValue: fullResolution
                                ? FULL_RESOLUTION_VALUE
                                : PREVIEW_RESOLUTION_VALUE,
                            ...(fullResolution
                                ? { mediaSizeToUse: VideoData.RENDER_MEDIA_SIZE }
                                : {}),
                        },
                    },
                );

                const content = await zip.generateAsync({ type: 'blob' });
                downloadBlob(content, `${videoName}_${fullResolution ? 'carousel' : 'scenes'}.zip`);
                analytics.track('ZIP Previews Button Clicked', { full_resolution: fullResolution });
            } catch (err) {
                Toast.error('Download failed.');
                ErrorReporter.capture(err);
                analytics.track('ZIP Previews Button Failed', {
                    full_resolution: fullResolution,
                });
            } finally {
                setDownloading(false);
            }
        },
        [fullResolution],
    );

    return { download, downloading };
}

export function useScenePreviewsZipDownload() {
    return useSceneImagesZipDownload({ fullResolution: false });
}

/**
 * Full-resolution scene stills ("carousel images"). Requires a published video: `RENDER_MEDIA_SIZE`
 * resolves to each asset's `renderUrl`, which only the render pipeline injects, and MediaAsset
 * throws outright when it is missing -- so callers must gate this on the video being PUBLISHED.
 */
export function useCarouselImagesZipDownload() {
    return useSceneImagesZipDownload({ fullResolution: true });
}

/**
 * Carousel is a paid feature, available from Basic up. There is no carousel-specific capability on
 * the backend, and the render_* capabilities don't line up with that boundary (render_1080p is off
 * on Basic, render_720p is on even for Free), so this keys off the plan directly.
 */
export function canDownloadCarouselImages(): boolean {
    return SERVER.ANALYTICS?.plan_name !== FREE_PLAN_NAME;
}

/** Encodes the video's full mixed audio track (music + voiceover) to a .wav. */
export function useAudioWavDownload() {
    const [downloading, setDownloading] = useState(false);

    const download = useCallback(async ({ videoData, videoName }: VideoArtifact) => {
        setDownloading(true);
        try {
            const duration = await withDataLuminary(
                async (dataLuminary) => {
                    // Untimed on purpose: a download is user-initiated and may legitimately load
                    // for minutes.
                    dataLuminary.load(videoData);
                    await dataLuminary.onCanPlay();
                    return dataLuminary.duration;
                },
                {
                    luminarySettings: {
                        fetchResources: {
                            audio: true,
                            media: false,
                            transitions: false,
                            fonts: false,
                        },
                    },
                },
            );

            if (!duration || duration <= 0) {
                throw new Error('Video has no audio or invalid duration');
            }

            const encoder = new OfflineAudioEncoder();
            encoder.setBasename(`${videoName}_audio`);
            encoder.setStartTime(0);
            encoder.setStopTime(duration);

            encoder.luminary.load(videoData);
            await encoder.encode();
            encoder.download();

            Toast.success('Audio download started!');
            analytics.track('Audio WAV Download Button Clicked');
        } catch (err) {
            Toast.error('Audio download failed.');
            ErrorReporter.capture(err);
            analytics.track('Audio WAV Download Button Failed');
        } finally {
            setDownloading(false);
        }
    }, []);

    return { download, downloading };
}

function downloadBlob(blob: Blob, fileName: string) {
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = fileName;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
}
